function vnodeName(vnode) {
    if (!vnode?.type) return null;
    if (typeof vnode.type === 'string') return null;
    return vnode.type.name || vnode.type.__name || null;
}

function flattenVnodes(vnodes) {
    const out = [];
    const list = Array.isArray(vnodes) ? vnodes : vnodes ? [vnodes] : [];
    for (const v of list) {
        if (!v) continue;
        if (v.type === Symbol.for('v-fgt') || v.type?.toString?.() === 'Symbol(Fragment)') {
            out.push(...flattenVnodes(v.children));
            continue;
        }
        out.push(v);
    }
    return out;
}

/**
 * @param {import('vue').VNode[] | import('vue').VNode} slotVnodes
 */
export function collectTabGroupPanelIdsFromVnodes(slotVnodes) {
    const ids = [];
    for (const v of flattenVnodes(slotVnodes)) {
        if (vnodeName(v) === 'MultipanelDockPanel' && v.props?.id) {
            ids.push(v.props.id);
        }
    }
    return ids;
}

/**
 * @param {import('vue').VNode[] | import('vue').VNode} slotVnodes
 */
export function collectZoneBlueprintFromVnodes(slotVnodes) {
    const children = [];
    for (const v of flattenVnodes(slotVnodes)) {
        const name = vnodeName(v);
        if (name === 'MultipanelDockPanel' && v.props?.id) {
            children.push({ type: 'panel', panelId: v.props.id });
        } else if (name === 'MultipanelDockTabGroup') {
            const panelIds = collectTabGroupPanelIdsFromVnodes(v.children);
            children.push({
                type: 'tabGroup',
                groupId: v.props?.id,
                tabPlacement: v.props?.tabPlacement,
                dynamic: !!v.props?.dynamic,
                contentPanelId: v.props?.id
                    ? `${v.props.id}__content`
                    : undefined,
                panelIds,
                activePanelId: v.props?.activeTab,
                title: v.props?.title,
            });
        } else if (name === 'MultipanelDockZone') {
            const nested = collectZoneBlueprintFromVnodes(v.children);
            children.push({
                type: 'split',
                orientation: v.props?.orientation || 'horizontal',
                sizes: v.props?.sizes,
                children: nested.length === 1 && nested[0].type !== 'split'
                    ? nested
                    : nested,
            });
        }
    }
    return children;
}

/**
 * Build a region blueprint from a zone block and its registered children.
 * @param {{ layout: string, orientation: string, sizes: number[]|null, children?: object[] }} block
 * @param {Record<string, object>} tabGroupsByKey
 */
export function buildZoneBlueprintFromBlock(block, tabGroupsByKey = {}) {
    const kids = (block.children || [])
        .map((entry) => {
            if (entry.kind === 'panel' && entry.id) {
                return { type: 'panel', panelId: entry.id };
            }
            if (entry.kind === 'tabGroup' && entry.key) {
                const tg = tabGroupsByKey[entry.key];
                if (!tg?.panelIds?.length) return null;
                return {
                    type: 'tabGroup',
                    groupId: tg.groupId,
                    tabPlacement: tg.tabPlacement,
                    dynamic: tg.dynamic,
                    contentPanelId: tg.contentPanelId,
                    panelIds: tg.panelIds,
                    activePanelId: tg.activePanelId,
                    title: tg.title,
                };
            }
            return null;
        })
        .filter(Boolean);

    if (block.layout === 'split') {
        if (kids.length === 1) return kids[0];
        return {
            type: 'split',
            orientation: block.orientation || 'horizontal',
            sizes: block.sizes,
            children: kids,
        };
    }
    if (kids.length === 1) return kids[0];
    const panelIds = [];
    for (const ch of kids) {
        if (ch.type === 'panel') panelIds.push(ch.panelId);
        else if (ch.type === 'tabGroup') {
            return kids.length === 1
                ? ch
                : {
                      type: 'split',
                      orientation: 'vertical',
                      children: kids,
                  };
        }
    }
    if (panelIds.length) {
        return {
            type: 'tabGroup',
            panelIds,
            tabPlacement: 'bottom',
        };
    }
    return kids[0] || null;
}

/** @deprecated use buildZoneBlueprintFromBlock */
export function zoneBlockToBlueprintNode(block) {
    return buildZoneBlueprintFromBlock(block, {});
}
